import {
    CalendarExternalEventGetResponse,
    Ulid,
} from "@kase/my-marathon-communication";
import { upsert } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createEmitter, createListController } from "../utils";
import { CalendarExternalEvent, UpdateExternalRequest } from "./interfaces";
import { getRangeKey, getUserRangeKey } from "./utils";

export const makeExternal = ({ connection, company }: ApiSliceParams) => {
    const loadedStore: Record<string, boolean> = {};
    const { emit, on, off } = createEmitter<CalendarExternalEvent>();

    const view = (external: CalendarExternalEventGetResponse) => ({
        ...external,
        fee_code:
            external.fee_code && Ulid.ZERO.equals(external.fee_code)
                ? undefined
                : external.fee_code,
        lob:
            external.lob && Ulid.ZERO.equals(external.lob)
                ? undefined
                : external.lob,
        project:
            external.project && Ulid.ZERO.equals(external.project)
                ? undefined
                : external.project,
        _id: external.id,
        id: external.id.toString(),
    });

    const {
        items: externals,
        loading,
        loaded,
        listen,
        get,
        getOrLoad,
        getOrForceLoad,
    } = createListController<
        CalendarExternalEventGetResponse,
        CalendarExternalEvent,
        { start: Date; end: Date }
    >({
        name: "external",
        connection,
        list: (con, { start, end }) =>
            con.time.calendar.external.event.items({
                start: BigInt(start.getTime()),
                stop: BigInt(end.getTime()),
            }),
        load: (con, id) =>
            con.time.calendar.external.event.get({
                id: Array.isArray(id) ? id : [id],
            }),
        getItem: (con, id) => con.time.calendar.external.event.getItem(id),
        getKey: ({ start, end }) => getRangeKey(start, end),
        emit,
        view,
    });

    const loadExternal = async (
        id: Readonly<Ulid> | readonly Readonly<Ulid>[],
    ) => {
        const con = connection.value;
        const finalId = Array.isArray(id) ? id : [id];
        const list = con.time.calendar.external.event.get({
            id: finalId,
        });
        const result: CalendarExternalEvent[] = [];

        try {
            for await (const item of list) {
                const finalExternal: CalendarExternalEvent = {
                    ...item,
                    fee_code:
                        item.fee_code && Ulid.ZERO.equals(item.fee_code)
                            ? undefined
                            : item.fee_code,
                    lob:
                        item.lob && Ulid.ZERO.equals(item.lob)
                            ? undefined
                            : item.lob,
                    project:
                        item.project && Ulid.ZERO.equals(item.project)
                            ? undefined
                            : item.project,
                    _id: item.id,
                    id: item.id.toString(),
                };

                result.push(finalExternal);
            }
        } catch (error: unknown) {
            console.log("[time.external.loadExternal]: error", error);
        }

        externals.value = upsert(externals.peek().slice(), result, "id");
        emit("add", result);

        return result;
    };

    // @TODO: review this when working on resource planning feature - how it fits into list controller concept
    const loadUser = async (userId: string, start: Date, end: Date) => {
        const key = getUserRangeKey(userId, start, end);
        const con = connection.value;
        const isLoading = loading({ start, end });

        if (!con || loadedStore[key] || isLoading.value) {
            return;
        }

        isLoading.value = true;

        const list = con.time.calendar.external.event.list({
            user: Ulid.fromString(userId),
            start: BigInt(start.getTime()),
            stop: BigInt(end.getTime()),
        });

        for await (const id of list) {
            await loadExternal(id);
        }

        loadedStore[key] = true;
        isLoading.value = false;
    };

    const hide = async (id: Readonly<Ulid>) => {
        const con = connection.value;

        return con.time.calendar.external.event.update({
            id,
            hidden: true,
        });
    };

    const update = async ({
        id,
        project,
        feeCategory,
        feeCode,
        comment,
    }: UpdateExternalRequest) => {
        const con = connection.value;

        if (!con) {
            throw new Error("No connection");
        }

        await con.time.calendar.external.event.update({
            id,
            project: project ?? Ulid.ZERO,
            lob: feeCategory ?? Ulid.ZERO,
            fee_code: feeCode ?? Ulid.ZERO,
            description: comment,
            company: company.value,
        });
    };

    return {
        externals,
        listen,
        loading,
        loaded,
        loadExternal,
        loadUser,
        update,
        hide,
        on,
        off,
        get,
        getOrLoad,
        getOrForceLoad,
    };
};
